/**
 * Dashboard Admin - Vue type Excel pour la gestion des inscriptions.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { inscriptionService, sejourService, documentService, aideService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { notify } from '../components/NotificationContainer';
import { prompt } from '../components/PromptContainer';
import { confirm } from '../components/ConfirmContainer';
import ToggleSwitch from '../components/ToggleSwitch';
import EmptyState from '../components/EmptyState';
import logger from '../utils/logger';
import './DashboardAdmin.css';

const DashboardAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inscriptions, setInscriptions] = useState([]);
  const [sejours, setSejours] = useState([]);
  const [documents, setDocuments] = useState({});
  const [aides, setAides] = useState({});

  // Charger les filtres depuis localStorage pour améliorer les performances
  // IMPORTANT: Le séjour est maintenant OBLIGATOIRE pour limiter les requêtes
  const [sejourFiltre, setSejourFiltre] = useState(() => {
    return localStorage.getItem('dashboardAdmin_sejourFiltre') || '';
  });
  const [trancheAgeFiltre, setTrancheAgeFiltre] = useState(() => {
    return localStorage.getItem('dashboardAdmin_trancheAgeFiltre') || '';
  });
  // Par défaut OFF - ne pas persister ces options pour éviter confusion
  const [afficherAnnulees, setAfficherAnnulees] = useState(false);
  const [afficherSejoursArchives, setAfficherSejoursArchives] = useState(false);

  // État pour la recherche par nom/prénom
  const [modeRecherche, setModeRecherche] = useState(() => {
    return localStorage.getItem('dashboardAdmin_modeRecherche') || 'sejour';
  });
  const [rechercheEnfant, setRechercheEnfant] = useState('');
  const [rechercheEnCours, setRechercheEnCours] = useState(false);

  // Refs pour optimisation recherche (debounce + abort)
  const searchTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const [editingCell, setEditingCell] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [downloadTypeDocument, setDownloadTypeDocument] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [mobileViewMode, setMobileViewMode] = useState('cards'); // 'cards' ou 'table'
  const [nombreInscriptionsAnnulees, setNombreInscriptionsAnnulees] = useState(0);

  // Sauvegarder les filtres dans localStorage quand ils changent
  useEffect(() => {
    localStorage.setItem('dashboardAdmin_sejourFiltre', sejourFiltre);
  }, [sejourFiltre]);

  useEffect(() => {
    localStorage.setItem('dashboardAdmin_trancheAgeFiltre', trancheAgeFiltre);
  }, [trancheAgeFiltre]);

  useEffect(() => {
    localStorage.setItem('dashboardAdmin_modeRecherche', modeRecherche);
  }, [modeRecherche]);

  // Calculer l'âge de l'enfant à la date de début du séjour
  const calculateAgeAtSejour = (dateNaissance, dateDebut) => {
    if (!dateNaissance || !dateDebut) return null;

    const birthDate = new Date(dateNaissance);
    const sejourDate = new Date(dateDebut);

    let age = sejourDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = sejourDate.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && sejourDate.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  // Vérifier si l'âge de l'enfant est hors de la tranche d'âge du séjour
  const isAgeOutOfRange = (inscription) => {
    if (!inscription.enfant_details?.date_naissance) return false;

    const sejour = sejours.find(s => s.id === inscription.sejour);
    if (!sejour || !sejour.date_debut) return false;

    const ageAtSejour = calculateAgeAtSejour(
      inscription.enfant_details.date_naissance,
      sejour.date_debut
    );

    if (ageAtSejour === null) return false;

    // Vérifier si l'enfant est inscrit dans une tranche spécifique
    // Utiliser tranche_prix_details en priorité, puis les champs figés inscription_age_min/max
    if (inscription.tranche_prix_details) {
      const trancheMin = inscription.tranche_prix_details.age_min;
      const trancheMax = inscription.tranche_prix_details.age_max;

      if (ageAtSejour < trancheMin || ageAtSejour > trancheMax) {
        return true;
      }
    } else if (inscription.inscription_age_min !== null && inscription.inscription_age_max !== null) {
      // Fallback sur les champs figés si la tranche n'existe plus (supprimée)
      const trancheMin = inscription.inscription_age_min;
      const trancheMax = inscription.inscription_age_max;

      if (ageAtSejour < trancheMin || ageAtSejour > trancheMax) {
        return true;
      }
    }

    // Cas 2: Vérifier si l'âge est hors de la tranche globale du séjour
    const ageMin = sejour.age_min;
    const ageMax = sejour.age_max;

    return ageAtSejour < ageMin || ageAtSejour > ageMax;
  };

  // Charger les séjours au montage
  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadSejours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Fonction pour la recherche par nom/prénom avec debounce
  useEffect(() => {
    if (!user || user.role !== 'ADMIN') return;
    if (modeRecherche !== 'enfant') return;

    // Annuler toute recherche en cours
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Valider minimum 3 caractères
    if (rechercheEnfant.length > 0 && rechercheEnfant.length < 3) {
      return;
    }

    if (!rechercheEnfant) {
      setInscriptions([]);
      setDocuments({});
      setAides({});
      return;
    }

    // Debounce: attendre 400ms après la dernière frappe
    searchTimerRef.current = setTimeout(() => {
      rechercherParEnfant(rechercheEnfant);
    }, 400);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheEnfant, modeRecherche, afficherAnnulees]);

  const rechercherParEnfant = async (query) => {
    // Annuler requête précédente si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Créer nouveau controller pour cette requête
    abortControllerRef.current = new AbortController();

    setRechercheEnCours(true);
    setLoading(true);

    try {
      const params = { q: query };
      if (afficherAnnulees) {
        params.afficher_annules = 'true';
      }

      const response = await inscriptionService.rechercheEnfant(params, {
        signal: abortControllerRef.current.signal
      });

      const inscriptionsData = response.data.inscriptions || [];
      setInscriptions(inscriptionsData);
      setDocuments({});
      setAides({});

      logger.log(`🔍 Recherche: ${inscriptionsData.length} résultats pour '${query}'`);
    } catch (error) {
      // Ignorer les erreurs d'annulation
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }

      logger.error('Erreur recherche:', error);
      if (error.response?.status === 400) {
        notify.warning(error.response.data.error || 'Minimum 3 caractères requis');
      } else {
        notify.error('Erreur lors de la recherche');
      }
      setInscriptions([]);
      setDocuments({});
      setAides({});
    } finally {
      setLoading(false);
      setRechercheEnCours(false);
    }
  };

  // Charger les données uniquement si un séjour est sélectionné
  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      return;
    }

    // Si mode recherche par enfant, on ne charge pas par séjour
    if (modeRecherche === 'enfant') {
      return;
    }

    if (sejourFiltre) {
      loadData(afficherAnnulees);
    } else {
      // Réinitialiser les inscriptions si aucun séjour n'est sélectionné
      setInscriptions([]);
      setDocuments({});
      setAides({});
      setNombreInscriptionsAnnulees(0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sejourFiltre, afficherAnnulees, modeRecherche]);

  // Charger uniquement la liste des séjours
  const loadSejours = async () => {
    try {
      setLoading(true);
      const response = await sejourService.getAll();
      logger.log('📋 Réponse API séjours:', response);
      logger.log('📋 response.data:', response.data);

      // Gérer différents formats de réponse
      const sejoursData = response.data?.results || response.data || [];
      logger.log('📋 Séjours extraits:', sejoursData);

      if (!Array.isArray(sejoursData)) {
        logger.error('❌ Les séjours ne sont pas un tableau:', sejoursData);
        let details = '';
        try {
          if (typeof sejoursData === 'object' && sejoursData !== null) {
            details = `Keys: ${Object.keys(sejoursData).join(', ')}`;
            if (sejoursData.detail) details += ` | Detail: ${sejoursData.detail}`;
            if (sejoursData.error) details += ` | Error: ${sejoursData.error}`;
          } else {
            details = `Type: ${typeof sejoursData}`;
          }
        } catch (e) {
          details = 'Unreadable data';
        }
        notify.error(`Format de données invalide pour les séjours. ${details}`);
        setSejours([]);
      } else {
        setSejours(sejoursData);
        logger.log(`✅ ${sejoursData.length} séjours chargés`);

        // LOG DÉTAILLÉ de tous les séjours
        logger.log('🔍 === DÉTAILS COMPLETS DES SÉJOURS ===');
        sejoursData.forEach(s => {
          logger.log(`  - ID: ${s.id}, Titre: "${s.titre}", archive: ${s.archive}, partenaires:`, s.partenaires);
        });

        const archivesTrue = sejoursData.filter(s => s.archive === true);
        const archivesFalse = sejoursData.filter(s => s.archive === false);
        const archivesUndefined = sejoursData.filter(s => s.archive === undefined);
        const archivesNull = sejoursData.filter(s => s.archive === null);

        logger.log('📊 Séjours avec archive === true:', archivesTrue.length, archivesTrue.map(s => s.titre));
        logger.log('📊 Séjours avec archive === false:', archivesFalse.length, archivesFalse.map(s => s.titre));
        logger.log('📊 Séjours avec archive === undefined:', archivesUndefined.length, archivesUndefined.map(s => s.titre));
        logger.log('📊 Séjours avec archive === null:', archivesNull.length, archivesNull.map(s => s.titre));
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des séjours:', error);
      notify.error('Erreur lors du chargement des séjours');
      setSejours([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données pour un séjour spécifique
  const loadData = async (includeAnnulees) => {
    if (!sejourFiltre) {
      return; // Ne rien charger si aucun séjour n'est sélectionné
    }

    try {
      setLoading(true);

      // OPTIMISÉ: Charger uniquement les inscriptions du séjour sélectionné
      const response = await inscriptionService.tableauBord();
      const data = response.data;

      // La réponse contient tout : inscriptions, sejours, documents, aides
      let inscriptionsData = data.inscriptions || [];
      const sejoursData = data.sejours || [];
      const allDocuments = data.documents || [];
      const allAides = data.aides || [];

      // Filtrer les inscriptions par séjour AVANT tout traitement
      inscriptionsData = inscriptionsData.filter(insc => insc.sejour === parseInt(sejourFiltre));

      // Compter le nombre d'inscriptions annulées pour l'affichage
      const nbAnnulees = inscriptionsData.filter(insc => insc.statut === 'ANNULEE').length;
      setNombreInscriptionsAnnulees(nbAnnulees);

      // Filtrer les inscriptions annulées côté frontend si nécessaire
      if (!includeAnnulees) {
        inscriptionsData = inscriptionsData.filter(insc => insc.statut !== 'ANNULEE');
      }

      // Mapper les séjours pour ajouter sejour_details aux inscriptions
      const sejoursById = {};
      sejoursData.forEach(sejour => {
        sejoursById[sejour.id] = sejour;
      });

      // Enrichir chaque inscription avec sejour_details
      const enrichedInscriptions = inscriptionsData.map(inscription => ({
        ...inscription,
        sejour_details: sejoursById[inscription.sejour] || null
      }));

      setInscriptions(enrichedInscriptions);

      // IMPORTANT: Mettre à jour les séjours pour avoir les tranches_prix
      // Si sejoursData contient des tranches_prix, on met à jour
      if (sejoursData.length > 0) {
        // Vérifier si au moins un séjour a des tranches_prix
        const hasTranchesInfo = sejoursData.some(s => s.tranches_prix !== undefined);
        if (hasTranchesInfo) {
          setSejours(sejoursData);
          logger.log('✅ Séjours mis à jour avec tranches_prix depuis tableau_bord');
        } else if (sejours.length === 0) {
          // Fallback: si les séjours sont vides et qu'on n'a pas les tranches, on met quand même à jour
          setSejours(sejoursData);
        }
      }

      // Grouper les documents et aides par inscription_id (uniquement pour les inscriptions du séjour)
      const documentsData = {};
      const aidesData = {};

      inscriptionsData.forEach(inscription => {
        documentsData[inscription.id] = [];
        aidesData[inscription.id] = [];
      });

      allDocuments.forEach(doc => {
        if (doc.inscription_id && documentsData[doc.inscription_id] !== undefined) {
          documentsData[doc.inscription_id].push(doc);
        }
      });

      allAides.forEach(aide => {
        if (aide.inscription_id && aidesData[aide.inscription_id] !== undefined) {
          aidesData[aide.inscription_id].push(aide);
        }
      });

      setDocuments(documentsData);
      setAides(aidesData);

      logger.log(`🚀 Dashboard chargé pour le séjour ${sejourFiltre}: ${inscriptionsData.length} inscriptions`);
    } catch (error) {
      logger.error('Erreur lors du chargement des données:', error);
      notify.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir les tranches d'âge du séjour sélectionné
  const getTranchesAge = () => {
    const tranches = [];
    if (Array.isArray(sejours) && sejourFiltre) {
      const sejourSelectionne = sejours.find(s => s.id === parseInt(sejourFiltre));

      if (sejourSelectionne) {
        // Si le séjour a des tranches de prix définies, les utiliser
        if (sejourSelectionne.tranches_prix && Array.isArray(sejourSelectionne.tranches_prix) && sejourSelectionne.tranches_prix.length > 0) {
          sejourSelectionne.tranches_prix.forEach(tranche => {
            tranches.push({
              label: `${tranche.age_min}-${tranche.age_max} ans`,
              value: `${tranche.age_min}-${tranche.age_max}`,
              id: tranche.id
            });
          });
        } else {
          // Sinon, utiliser la tranche globale du séjour
          tranches.push({
            label: `${sejourSelectionne.age_min}-${sejourSelectionne.age_max} ans`,
            value: `${sejourSelectionne.age_min}-${sejourSelectionne.age_max}`,
            id: null
          });
        }
      }
    }

    // Trier par age_min
    return tranches.sort((a, b) => {
      const ageMinA = parseInt(a.value.split('-')[0]);
      const ageMinB = parseInt(b.value.split('-')[0]);
      return ageMinA - ageMinB;
    });
  };

  // Gérer le tri des colonnes
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Inverser la direction si on clique sur la même colonne
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouvelle colonne, tri ascendant par défaut
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Fonction pour obtenir la valeur de tri
  const getSortValue = (inscription, column) => {
    switch (column) {
      case 'prenom':
        return inscription.enfant_details?.prenom?.toLowerCase() || '';
      case 'nom':
        return inscription.enfant_details?.nom?.toLowerCase() || '';
      case 'age':
        return inscription.enfant_details?.age || 0;
      case 'date_naissance':
        return inscription.enfant_details?.date_naissance || '';
      case 'lieu_naissance':
        return inscription.enfant_details?.lieu_naissance?.toLowerCase() || '';
      case 'responsable':
        return `${inscription.responsable_prenom || ''} ${inscription.responsable_nom || ''}`.toLowerCase();
      case 'adresse':
        return inscription.responsable_adresse?.toLowerCase() || '';
      case 'telephone':
        return inscription.responsable_telephone || '';
      case 'email':
        return inscription.responsable_email?.toLowerCase() || '';
      case 'statut':
        return inscription.statut || '';
      default:
        return '';
    }
  };

  // Filtrer et trier les inscriptions
  const inscriptionsFiltrees = inscriptions
    .filter((inscription) => {
      // Le filtrage des inscriptions annulées est maintenant fait lors du chargement des données
      // On garde seulement les filtres par séjour et tranche d'âge

      // IMPORTANT: Les filtres par séjour et tranche d'âge ne s'appliquent qu'en mode 'sejour'
      // En mode 'enfant', les inscriptions sont déjà filtrées par l'API backend
      if (modeRecherche === 'sejour') {
        // Filtre par séjour
        if (sejourFiltre && inscription.sejour !== parseInt(sejourFiltre)) {
          return false;
        }

        // Filtre par tranche d'âge
        if (trancheAgeFiltre) {
          // Log pour debug
          logger.log(`[FILTRE TRANCHE] Inscription #${inscription.id}:`, {
            tranche_prix_id: inscription.tranche_prix,
            tranche_prix_details: inscription.tranche_prix_details,
            filtre_actuel: trancheAgeFiltre
          });

          // Vérifier d'abord si l'inscription a une tranche de prix spécifique
          if (inscription.tranche_prix_details) {
            const trancheInscription = `${inscription.tranche_prix_details.age_min}-${inscription.tranche_prix_details.age_max}`;
            logger.log(`  → Tranche trouvée: ${trancheInscription}, filtre: ${trancheAgeFiltre}, match: ${trancheInscription === trancheAgeFiltre}`);
            if (trancheInscription !== trancheAgeFiltre) {
              return false;
            }
          } else if (Array.isArray(sejours)) {
            // Sinon, utiliser la tranche globale du séjour
            const sejour = sejours.find((s) => s.id === inscription.sejour);
            if (sejour) {
              const trancheSejour = `${sejour.age_min}-${sejour.age_max}`;
              logger.log(`  → Pas de tranche_prix_details, utilisation tranche séjour: ${trancheSejour}`);
              if (trancheSejour !== trancheAgeFiltre) {
                return false;
              }
            }
          } else {
            logger.log(`  → Aucune tranche trouvée, inscription exclue`);
            return false;
          }
        }
      }

      return true;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;

      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Mettre à jour une inscription (sans recharger toute la page)
  const updateInscription = async (id, data) => {
    try {
      const response = await inscriptionService.update(id, data);

      // Mettre à jour uniquement l'inscription modifiée dans le state
      setInscriptions((prevInscriptions) =>
        prevInscriptions.map((inscription) =>
          inscription.id === id ? { ...inscription, ...response.data } : inscription
        )
      );
    } catch (error) {
      logger.error('Erreur lors de la mise à jour:', error);
      notify.error('Erreur lors de la mise à jour');
    }
  };

  // Gérer le changement de statut de validation
  const handleValidationChange = async (inscription, newStatut) => {
    // Ne rien faire si le statut n'a pas changé
    if (inscription.statut === newStatut) {
      return;
    }

    // Obtenir le libellé du statut pour l'affichage
    const getStatutLabel = (statut) => {
      switch (statut) {
        case 'EN_ATTENTE': return 'En attente';
        case 'CONFIRMEE': return 'Validé';
        case 'TERMINE': return 'Terminé';
        case 'ANNULEE': return 'Annulée';
        case 'EN_ATTENTE_VALIDATION_PARTENAIRE': return 'En attente partenaire';
        default: return statut;
      }
    };

    // Demander confirmation pour le changement de statut
    const confirmChange = await confirm.ask(
      `Voulez-vous vraiment changer le statut de cette inscription ?\n\nDe : ${getStatutLabel(inscription.statut)}\nVers : ${getStatutLabel(newStatut)}\n\nEnfant : ${inscription.enfant_details?.prenom || ''} ${inscription.enfant_details?.nom || ''}`,
      'Confirmer le changement de statut'
    );

    if (!confirmChange) {
      return;
    }

    // Si on passe vers "En attente partenaire"
    if (newStatut === 'EN_ATTENTE_VALIDATION_PARTENAIRE') {
      // Si l'inscription est déjà via partenaire et non validée, demander de valider
      if (inscription.via_partenaire && !inscription.partenaire_valide) {
        const choice = await confirm.ask('Voulez-vous valider ce partenaire ?\n\nOK = Valider\nAnnuler = Refuser', 'Validation partenaire');

        if (choice) {
          await handleValiderPartenaire(inscription);
        } else {
          await handleRefuserPartenaire(inscription);
        }
      } else {
        // Sinon, demander le nom du partenaire pour créer une inscription via partenaire
        const nomPartenaire = await prompt.askRequired('Nom du partenaire social (organisme) :', 'Saisir le nom de l\'organisme', 'Nom du partenaire');

        if (nomPartenaire) {
          try {
            const response = await inscriptionService.update(inscription.id, {
              statut: newStatut,
              via_partenaire: true,
              partenaire_affilie: nomPartenaire,
              partenaire_valide: false
            });

            // Mettre à jour l'état local
            setInscriptions((prevInscriptions) =>
              prevInscriptions.map((insc) =>
                insc.id === inscription.id
                  ? { ...insc, ...response.data }
                  : insc
              )
            );
            notify.success('Statut mis à jour avec succès');
          } catch (error) {
            logger.error('Erreur lors de la mise à jour:', error);
            notify.error('Erreur lors de la mise à jour');
          }
        }
      }
    } else {
      // Changement de statut normal
      try {
        await updateInscription(inscription.id, { statut: newStatut });
        notify.success('Statut mis à jour avec succès');
      } catch (error) {
        logger.error('Erreur lors de la mise à jour:', error);
        notify.error('Erreur lors de la mise à jour');
      }
    }
  };

  // Gérer l'édition de l'aide financière
  const handleAideChange = async (inscription, value) => {
    const match = value.match(/^(.+?)\s*\((\d+(?:\.\d+)?)\s*€?\)$/);

    if (!match) {
      notify.warning('Format invalide. Utilisez le format: Type (Montant)\nExemple: CAF (100)');
      return;
    }

    const type = match[1].trim();
    const montant = parseFloat(match[2]);

    try {
      await inscriptionService.setAide(inscription.id, montant, type);

      // Mettre à jour uniquement l'aide dans l'inscription
      setInscriptions((prevInscriptions) =>
        prevInscriptions.map((insc) =>
          insc.id === inscription.id
            ? {
              ...insc,
              aide_accordee: montant,
              aide_commentaire: type,
              prix_final: (insc.montant_total || 0) - montant,
              solde_restant: Math.max(0, (insc.montant_total || 0) - montant - (insc.montant_paye || 0))
            }
            : insc
        )
      );
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de l\'aide:', error);
      notify.error('Erreur lors de la mise à jour de l\'aide');
    }
  };

  // Formater l'affichage des aides (lecture seule)
  const formatAides = (inscription) => {
    const inscriptionAides = aides[inscription.id] || [];
    // Filtrer uniquement les aides accordées
    const aidesAccordees = inscriptionAides.filter(aide => aide.statut_accord === 'ACCORDE');

    if (aidesAccordees.length === 0) {
      return '-';
    }

    // Format: ORGANISME(MONTANT) ; ORGANISME2(MONTANT2)
    return aidesAccordees
      .map(aide => `${aide.organisme}(${aide.montant}€)`)
      .join(' ; ');
  };

  // Version compacte pour les colonnes du tableau : première aide + "+N" si plusieurs
  const formatAidesCompact = (inscription) => {
    const inscriptionAides = aides[inscription.id] || [];
    const aidesAccordees = inscriptionAides.filter(aide => aide.statut_accord === 'ACCORDE');

    if (aidesAccordees.length === 0) {
      return <span className="text-gray-400">-</span>;
    }

    const premiere = `${aidesAccordees[0].organisme}(${aidesAccordees[0].montant}€)`;
    const tooltipComplet = aidesAccordees
      .map(aide => `${aide.organisme}(${aide.montant}€)`)
      .join('\n');

    return (
      <span title={tooltipComplet} className="cursor-help">
        {premiere}
        {aidesAccordees.length > 1 && (
          <span className="ml-1 text-primary-600 font-medium">+{aidesAccordees.length - 1}</span>
        )}
      </span>
    );
  };

  // Montant des aides dont le paiement a été validé (affichage uniquement,
  // ne modifie pas montant_paye/solde_restant utilisés ailleurs)
  const getMontantAidesPayees = (inscription) => {
    const inscriptionAides = aides[inscription.id] || [];
    return inscriptionAides
      .filter(aide => aide.statut_paiement === 'PAYE')
      .reduce((sum, aide) => sum + (parseFloat(aide.montant) || 0), 0);
  };

  // Formater l'affichage du paiement (fonction unifiée)
  const formatPaiement = (inscription, options = {}) => {
    const { showReste = false, showDetails = false } = options;
    const total = inscription.montant_total || 0;
    const paye = (inscription.montant_paye || 0) + getMontantAidesPayees(inscription);
    const reste = inscription.solde_restant || 0;

    let result = `${paye}€/${total}€`;

    if (showReste && reste > 0) {
      result += ` (reste ${reste}€)`;
    }

    return result;
  };

  // Obtenir les détails du paiement pour le tooltip
  const getPaiementDetails = (inscription) => {
    const total = inscription.montant_total || 0;
    const payeFamille = inscription.montant_paye || 0;
    const aidePayee = getMontantAidesPayees(inscription);
    const paye = payeFamille + aidePayee;
    const aide = inscription.aide_accordee || 0;
    const reste = inscription.solde_restant || 0;

    return `Total: ${total}€\nPayé famille: ${payeFamille}€\nAide payée: ${aidePayee}€\nPayé (famille + aide): ${paye}€\nAide accordée: ${aide}€\nReste: ${reste}€`;
  };

  // Vérifier le statut des infos d'inscription (avec emoji + sablier si vérification CAF en attente)
  const getInfosStatus = (inscription) => {
    const hasNumeroCAF = inscription.numero_msa_caf && inscription.numero_msa_caf.trim() !== '';

    // Vérifier si une aide CAF existe déjà pour cette inscription
    const inscriptionAides = aides[inscription.id] || [];
    const hasAideCAF = inscriptionAides.some(aide => aide.type_aide === 'CAF');

    // Le sablier s'affiche si : numéro CAF renseigné, statut EN_ATTENTE, et aucune aide CAF n'existe
    const cafEnAttente = hasNumeroCAF && inscription.statut_verification_caf === 'EN_ATTENTE' && !hasAideCAF;

    if (inscription.is_infos_complete) {
      return (
        <span>
          <span className="text-green-600 font-bold">✓</span>
          {cafEnAttente && (
            <span className="ml-1" title="Vérification CAF en attente">
              ⏳
            </span>
          )}
        </span>
      );
    } else {
      return (
        <span>
          <span className="text-red-600 font-bold">✗</span>
          {cafEnAttente && (
            <span className="ml-1" title="Vérification CAF en attente">
              ⏳
            </span>
          )}
        </span>
      );
    }
  };

  // Vérifier si le dossier est complet (infos + tous les documents validés)
  const getDossierCompletStatus = (inscription) => {
    // Vérifier les informations
    if (!inscription.is_infos_complete) {
      return (
        <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 font-medium">
          Incomplet
        </span>
      );
    }

    // Vérifier les documents requis
    const inscriptionDocs = documents[inscription.id] || [];
    const typesRequis = ['FICHE_SANITAIRE', 'VACCINS', 'ATTESTATION_AISANCE_AQUATIQUE'];

    // Tous les documents requis doivent être validés (ou NON_OBTENU pour le pass nautique)
    const tousDocumentsValides = typesRequis.every(type => {
      const doc = inscriptionDocs.find(d => d.type_document === type);
      if (type === 'ATTESTATION_AISANCE_AQUATIQUE') {
        // Pour le pass nautique, accepter VALIDE ou NON_OBTENU
        return doc && (doc.statut_validation === 'VALIDE' || doc.statut_validation === 'NON_OBTENU');
      }
      return doc && doc.statut_validation === 'VALIDE';
    });

    if (tousDocumentsValides) {
      return (
        <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800 font-medium">
          Complet
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 font-medium">
          Incomplet
        </span>
      );
    }
  };

  // Obtenir les statuts des documents
  const getDocumentStatus = (inscription, type) => {
    const inscriptionDocs = documents[inscription.id] || [];
    const doc = inscriptionDocs.find((d) => d.type_document === type);

    if (!doc) {
      return <span className="text-red-600 font-bold">✗</span>;
    }

    switch (doc.statut_validation) {
      case 'VALIDE':
        return <span className="text-green-600 font-bold">✓</span>;
      case 'NON_OBTENU':
        // Afficher différemment pour le pass nautique
        if (type === 'ATTESTATION_AISANCE_AQUATIQUE') {
          return <span className="text-blue-600 font-bold" title="Non obtenu">⊘</span>;
        }
        return <span className="text-blue-600 font-bold">⊘</span>;
      case 'EN_ATTENTE':
        return <span className="text-yellow-600 font-bold cursor-pointer hover:text-yellow-800">⏳</span>;
      case 'REFUSE':
        return <span className="text-red-600 font-bold cursor-pointer hover:text-red-800">✗</span>;
      default:
        return <span className="text-gray-400">-</span>;
    }
  };

  // Obtenir le statut des documents autres
  const getDocumentsAutresStatus = (inscription) => {
    const inscriptionDocs = documents[inscription.id] || [];
    const docsAutres = inscriptionDocs.filter((d) => d.type_document === 'AUTRE');

    if (docsAutres.length === 0) {
      return <span className="text-gray-400">-</span>;
    }

    // Compter les statuts
    const valides = docsAutres.filter(d => d.statut_validation === 'VALIDE').length;
    const enAttente = docsAutres.filter(d => d.statut_validation === 'EN_ATTENTE').length;
    const refuses = docsAutres.filter(d => d.statut_validation === 'REFUSE').length;
    const total = docsAutres.length;

    // Afficher un résumé visuel
    return (
      <div className="flex items-center gap-1" title={`${total} document(s) autre(s) : ${valides} validé(s), ${enAttente} en attente, ${refuses} refusé(s)`}>
        <span className="text-xs font-medium">{total}</span>
        {valides > 0 && <span className="text-green-600 font-bold">✓{valides}</span>}
        {enAttente > 0 && <span className="text-yellow-600 font-bold">⏳{enAttente}</span>}
        {refuses > 0 && <span className="text-red-600 font-bold">✗{refuses}</span>}
      </div>
    );
  };

  // Vérifier s'il y a des documents en attente ou des paiements en attente
  const hasDocumentsEnAttente = (inscription) => {
    const inscriptionDocs = documents[inscription.id] || [];
    return inscriptionDocs.some(d => d.statut_validation === 'EN_ATTENTE');
  };

  const hasPaiementsEnAttente = (inscription) => {
    return inscription.solde_restant > 0 || false;
  };

  // Gérer la mise à jour du champ "Autre info utile"
  const handleAutreInfoChange = async (inscription, value) => {
    await updateInscription(inscription.id, { remarques_admin: value });
  };

  // Gérer l'édition de "Autre info utile" sur mobile (via prompt)
  const handleEditAutreInfoMobile = async (inscription) => {
    const newValue = await prompt.ask(
      'Autre information utile',
      'Informations complémentaires pour cette inscription',
      inscription.remarques_admin || ''
    );

    if (newValue !== null && newValue !== inscription.remarques_admin) {
      await handleAutreInfoChange(inscription, newValue);
      notify.success('Information mise à jour');
    }
  };

  // Composant pour l'en-tête de colonne triable
  const SortableHeader = ({ column, label, columnIndex }) => (
    <th
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100"
    >
      <div className="flex items-center space-x-1">
        <span className="cursor-pointer" onClick={() => handleSort(column)}>{label}</span>
        {sortColumn === column && (
          <span className="text-primary-600">
            {sortDirection === 'asc' ? '▲' : '▼'}
          </span>
        )}
        {columnIndex !== undefined && (
          <span
            className="copy-column-btn"
            onClick={(e) => {
              e.stopPropagation();
              copyColumn(columnIndex, label);
            }}
            title="Copier toute la colonne"
          >
            📋
          </span>
        )}
      </div>
    </th>
  );

  // Composant pour l'en-tête de colonne non triable avec copie
  const CopyableHeader = ({ label, columnIndex }) => (
    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100">
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <span
          className="copy-column-btn"
          onClick={(e) => {
            e.stopPropagation();
            copyColumn(columnIndex, label);
          }}
          title="Copier toute la colonne"
        >
          📋
        </span>
      </div>
    </th>
  );

  // Gérer la validation partenaire
  const handleValiderPartenaire = async (inscription) => {
    if (!await confirm.ask('Êtes-vous sûr de vouloir valider cette inscription partenaire ?', 'Valider l\'inscription')) return;

    try {
      await inscriptionService.validerPartenaire(inscription.id);

      // Mettre à jour l'état local
      setInscriptions((prevInscriptions) =>
        prevInscriptions.map((insc) =>
          insc.id === inscription.id
            ? { ...insc, partenaire_valide: true, statut: 'CONFIRMEE' }
            : insc
        )
      );

      notify.success('Inscription partenaire validée avec succès');
    } catch (error) {
      logger.error('Erreur lors de la validation:', error);
      notify.error('Erreur lors de la validation');
    }
  };

  const handleRefuserPartenaire = async (inscription) => {
    const motif = await prompt.askRefusalReason();
    if (!motif) return;

    try {
      await inscriptionService.refuserPartenaire(inscription.id, motif);

      // Mettre à jour l'état local
      setInscriptions((prevInscriptions) =>
        prevInscriptions.map((insc) =>
          insc.id === inscription.id
            ? { ...insc, statut: 'ANNULEE' }
            : insc
        )
      );

      notify.success('Inscription partenaire refusée');
    } catch (error) {
      logger.error('Erreur lors du refus:', error);
      alert('Erreur lors du refus');
    }
  };

  // Gérer les clics sur les cellules (pour éviter les clics lors de la sélection de texte)
  const handleCellClick = (e, callback) => {
    // Ne pas déclencher l'action si l'utilisateur est en train de sélectionner du texte
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    callback();
  };


  // Gérer le double-clic pour l'édition
  const handleDoubleClick = (inscriptionId, field) => {
    setEditingCell({ inscriptionId, field });
  };

  // Copier une colonne entière dans le presse-papier
  const copyColumn = (columnIndex, columnName) => {
    const table = document.querySelector('.excel-table');
    if (!table) return;

    const rows = table.querySelectorAll('tbody tr');
    const columnData = [];

    rows.forEach((row) => {
      const cell = row.cells[columnIndex];
      if (cell) {
        // Extraire le texte de la cellule
        let text = cell.innerText || cell.textContent || '';
        text = text.trim();
        // Sanitize: Remplacer les retours à la ligne par des espaces
        text = text.replace(/\n/g, ' ').replace(/\r/g, '');
        columnData.push(text);
      }
    });

    // Copier dans le presse-papier avec un saut de ligne entre chaque donnée
    // Cela permet un meilleur collage dans Excel/Sheets (une cellule par ligne)
    const textToCopy = columnData.join('\n');

    // Méthode compatible iOS/iPhone
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Méthode moderne (peut ne pas fonctionner sur iOS dans certains contextes)
      navigator.clipboard.writeText(textToCopy).then(() => {
        notify.success('Colonne copiée !');
      }).catch(err => {
        logger.error('Erreur lors de la copie:', err);
        // Fallback vers la méthode alternative
        copyToClipboardFallback(textToCopy);
      });
    } else {
      // Fallback pour les navigateurs qui ne supportent pas l'API Clipboard
      copyToClipboardFallback(textToCopy);
    }
  };

  // Méthode alternative pour copier dans le presse-papier (compatible iOS)
  const copyToClipboardFallback = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Rendre le textarea invisible mais sélectionnable
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    // Pour iOS
    textArea.setSelectionRange(0, textArea.value.length);

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        notify.success('Colonne copiée !');
      } else {
        notify.error('Impossible de copier');
      }
    } catch (err) {
      logger.error('Erreur lors de la copie:', err);
      notify.error('Impossible de copier');
    }

    document.body.removeChild(textArea);
  };

  // Gérer la perte de focus (fin d'édition)
  const handleBlur = async (inscription, field, value) => {
    setEditingCell(null);

    if (field === 'remarques_admin') {
      await handleAutreInfoChange(inscription, value);
    }
  };

  // Fonction pour télécharger les documents groupés en PDF
  const handleTelechargerDocumentsGroupes = async () => {
    if (inscriptionsFiltrees.length === 0) {
      notify.info('Aucune inscription affichée. Veuillez ajuster les filtres.');
      return;
    }

    try {
      setDownloading(true);

      // Récupérer les IDs des inscriptions filtrées affichées dans le tableau
      const inscriptionIds = inscriptionsFiltrees.map(i => i.id);

      // Faire la requête pour télécharger le PDF
      const response = await api.post('/documents/telecharger_groupe/', {
        inscription_ids: inscriptionIds,
        type_document: downloadTypeDocument || undefined
      }, {
        responseType: 'blob'
      });

      // Extraire le nom du fichier depuis les headers ou créer un nom par défaut
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'documents.pdf';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }

      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // Fermer le panneau après le téléchargement
      setShowDownloadPanel(false);

      // Réinitialiser les champs
      setDownloadTypeDocument('');

      notify.success('Téléchargement terminé avec succès !');
    } catch (error) {
      logger.error('Erreur lors du téléchargement:', error);
      if (error.response && error.response.status === 404) {
        notify.info('Aucun document trouvé pour les critères spécifiés');
      } else if (error.response && error.response.data) {
        // Tenter de lire le message d'erreur si c'est un blob
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            notify.error(errorData.error || 'Une erreur est survenue');
          } catch {
            notify.error('Erreur lors du téléchargement des documents');
          }
        };
        reader.readAsText(error.response.data);
      } else {
        notify.error('Erreur lors du téléchargement des documents');
      }
    } finally {
      setDownloading(false);
    }
  };

  // Fonction pour générer le trombinoscope
  const handleGenererTrombinoscope = async () => {
    if (inscriptionsFiltrees.length === 0) {
      notify.info('Aucune inscription affichée. Veuillez ajuster les filtres.');
      return;
    }

    try {
      setDownloading(true);

      // Récupérer les IDs des inscriptions filtrées affichées dans le tableau
      const inscriptionIds = inscriptionsFiltrees.map(i => i.id);

      // Récupérer les informations de filtre pour le titre
      let sejourTitre = '';
      if (sejourFiltre && Array.isArray(sejours)) {
        const sejour = sejours.find(s => s.id === parseInt(sejourFiltre));
        if (sejour) {
          sejourTitre = sejour.titre;
        }
      }

      // Faire la requête pour générer le trombinoscope
      const response = await api.post('/enfants/generer_trombinoscope/', {
        inscription_ids: inscriptionIds,
        sejour_titre: sejourTitre,
        tranche_age: trancheAgeFiltre
      }, {
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'trombinoscope.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      alert('Trombinoscope généré avec succès !');
    } catch (error) {
      logger.error('Erreur lors de la génération du trombinoscope:', error);
      if (error.response && error.response.data) {
        // Tenter de lire le message d'erreur si c'est un blob
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            notify.error(errorData.error || 'Une erreur est survenue');
          } catch {
            alert('Erreur lors de la génération du trombinoscope');
          }
        };
        reader.readAsText(error.response.data);
      } else {
        alert('Erreur lors de la génération du trombinoscope');
      }
    } finally {
      setDownloading(false);
    }
  };

  // Fonction pour télécharger tous les documents en ZIP organisés par tranche d'âge
  const handleTelechargerTousDocumentsZip = async () => {
    if (!sejourFiltre) {
      notify.info('Veuillez sélectionner un séjour d\'abord.');
      return;
    }

    try {
      setDownloading(true);

      // Récupérer les informations du séjour pour le message
      let sejourTitre = '';
      if (Array.isArray(sejours)) {
        const sejour = sejours.find(s => s.id === parseInt(sejourFiltre));
        if (sejour) {
          sejourTitre = sejour.titre;
        }
      }

      notify.info(`Génération du ZIP en cours pour le séjour "${sejourTitre}"...`);

      // Faire la requête pour télécharger le ZIP
      const response = await api.post('/documents/telecharger_tous_zip/', {
        sejour_id: parseInt(sejourFiltre)
      }, {
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Extraire le nom du fichier depuis les headers de la réponse ou utiliser un nom par défaut
      const contentDisposition = response.headers['content-disposition'];
      let filename = `documents_${sejourTitre.replace(/[^a-z0-9]/gi, '_')}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      notify.success('ZIP téléchargé avec succès ! Les documents sont organisés par tranche d\'âge.');
    } catch (error) {
      logger.error('Erreur lors du téléchargement du ZIP:', error);
      if (error.response && error.response.data) {
        // Tenter de lire le message d'erreur si c'est un blob
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            notify.error(errorData.error || 'Une erreur est survenue lors de la génération du ZIP');
          } catch {
            notify.error('Erreur lors de la génération du ZIP');
          }
        };
        reader.readAsText(error.response.data);
      } else {
        notify.error('Erreur lors de la génération du ZIP');
      }
    } finally {
      setDownloading(false);
    }
  };

  // Fonction d'export en CSV (compatible Google Sheets)
  const handleExportCSV = () => {
    // En-têtes du CSV
    const headers = [
      'Prénom',
      'NOM',
      'Age',
      'Date de naissance',
      'Lieu de naissance',
      'Responsable légal',
      'Adresse',
      'Téléphone',
      'Mail',
      'Validation',
      'Aide',
      'Paiement',
      'Dossier complet',
      'Informations',
      'Fiche sanitaire',
      'Baignade',
      'Vaccins',
      'Documents autres',
      'Autre info utile'
    ];

    // Fonction pour échapper les virgules et guillemets dans les cellules CSV
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const strValue = String(value);
      if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
        return `"${strValue.replace(/"/g, '""')}"`;
      }
      return strValue;
    };

    // Fonction pour obtenir le statut du document
    const getDocStatus = (inscription, type) => {
      const inscriptionDocs = documents[inscription.id] || [];
      const doc = inscriptionDocs.find((d) => d.type_document === type);
      if (!doc) return 'Manquant';
      switch (doc.statut_validation) {
        case 'VALIDE': return 'Validé';
        case 'NON_OBTENU': return 'Non obtenu';
        case 'EN_ATTENTE': return 'En attente';
        case 'REFUSE': return 'Refusé';
        default: return 'Inconnu';
      }
    };

    // Fonction pour obtenir le résumé des documents autres (pour CSV)
    const getDocumentsAutresStatusCSV = (inscription) => {
      const inscriptionDocs = documents[inscription.id] || [];
      const docsAutres = inscriptionDocs.filter((d) => d.type_document === 'AUTRE');

      if (docsAutres.length === 0) return 'Aucun';

      const valides = docsAutres.filter(d => d.statut_validation === 'VALIDE').length;
      const enAttente = docsAutres.filter(d => d.statut_validation === 'EN_ATTENTE').length;
      const refuses = docsAutres.filter(d => d.statut_validation === 'REFUSE').length;

      return `${docsAutres.length} doc(s): ${valides} validé(s), ${enAttente} en attente, ${refuses} refusé(s)`;
    };

    // Fonction pour vérifier si le dossier est complet (pour CSV)
    const getDossierComplet = (inscription) => {
      if (!inscription.is_infos_complete) return 'Incomplet';

      const inscriptionDocs = documents[inscription.id] || [];
      const typesRequis = ['FICHE_SANITAIRE', 'VACCINS', 'ATTESTATION_AISANCE_AQUATIQUE'];

      const tousDocumentsValides = typesRequis.every(type => {
        const doc = inscriptionDocs.find(d => d.type_document === type);
        if (type === 'ATTESTATION_AISANCE_AQUATIQUE') {
          // Pour le pass nautique, accepter VALIDE ou NON_OBTENU
          return doc && (doc.statut_validation === 'VALIDE' || doc.statut_validation === 'NON_OBTENU');
        }
        return doc && doc.statut_validation === 'VALIDE';
      });

      return tousDocumentsValides ? 'Complet' : 'Incomplet';
    };

    // Créer les lignes de données
    const rows = inscriptionsFiltrees.map((inscription) => {
      return [
        escapeCSV(inscription.enfant_details?.prenom || ''),
        escapeCSV(inscription.enfant_details?.nom?.toUpperCase() || ''),
        escapeCSV(inscription.enfant_details?.age || ''),
        escapeCSV(inscription.enfant_details?.date_naissance
          ? new Date(inscription.enfant_details.date_naissance).toLocaleDateString('fr-FR')
          : ''),
        escapeCSV(inscription.enfant_details?.lieu_naissance || ''),
        escapeCSV(inscription.responsable_nom && inscription.responsable_prenom
          ? `${inscription.responsable_prenom} ${inscription.responsable_nom}`
          : ''),
        escapeCSV(inscription.responsable_adresse
          ? `${inscription.responsable_adresse}, ${inscription.responsable_code_postal} ${inscription.responsable_ville}`
          : ''),
        escapeCSV(inscription.responsable_telephone || ''),
        escapeCSV(inscription.responsable_email || ''),
        escapeCSV(inscription.statut === 'CONFIRMEE' ? 'Validé' :
          inscription.statut === 'EN_ATTENTE' ? 'En attente' :
            inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' ? 'En attente partenaire' :
              'Annulée'),
        escapeCSV(formatAides(inscription)),
        escapeCSV(`${inscription.montant_paye || 0}€ / ${inscription.montant_total || 0}€`),
        escapeCSV(getDossierComplet(inscription)),
        escapeCSV(inscription.is_infos_complete ? 'Complet' : 'Incomplet'),
        escapeCSV(getDocStatus(inscription, 'FICHE_SANITAIRE')),
        escapeCSV(getDocStatus(inscription, 'ATTESTATION_AISANCE_AQUATIQUE')),
        escapeCSV(getDocStatus(inscription, 'VACCINS')),
        escapeCSV(getDocumentsAutresStatusCSV(inscription)),
        escapeCSV(inscription.remarques_admin || '')
      ].join(',');
    });

    // Combiner en-têtes et lignes
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Créer un blob et télécharger
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inscriptions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Dashboard Admin - Gestion des Inscriptions
        </h1>

        {/* Boutons d'export et téléchargement */}
        <div className="mb-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
          <button
            onClick={() => setShowDownloadPanel(!showDownloadPanel)}
            className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={modeRecherche === 'enfant' || !sejourFiltre}
            title={modeRecherche === 'enfant' ? 'Disponible uniquement en mode recherche par séjour' : (!sejourFiltre ? 'Sélectionnez un séjour d\'abord' : '')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>Télécharger documents PDF</span>
          </button>
          <button
            onClick={handleGenererTrombinoscope}
            className="bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={modeRecherche === 'enfant' || !sejourFiltre || downloading || inscriptionsFiltrees.length === 0}
            title={modeRecherche === 'enfant' ? 'Disponible uniquement en mode recherche par séjour' : (!sejourFiltre ? 'Sélectionnez un séjour d\'abord' : '')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>Générer trombinoscope</span>
          </button>
          <button
            onClick={handleExportCSV}
            className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={modeRecherche === 'enfant' || !sejourFiltre}
            title={modeRecherche === 'enfant' ? 'Disponible uniquement en mode recherche par séjour' : (!sejourFiltre ? 'Sélectionnez un séjour d\'abord' : '')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Exporter en CSV (Sheets)</span>
          </button>
          <button
            onClick={handleTelechargerTousDocumentsZip}
            className="bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={modeRecherche === 'enfant' || !sejourFiltre || downloading}
            title={modeRecherche === 'enfant' ? 'Disponible uniquement en mode recherche par séjour' : (!sejourFiltre ? 'Sélectionnez un séjour d\'abord' : 'Télécharge tous les documents dans un fichier ZIP organisé par tranche d\'âge')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            <span>Télécharger tous documents ZIP</span>
          </button>
        </div>

        {/* Panneau de téléchargement groupé */}
        {showDownloadPanel && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg shadow p-6 mb-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">
              Télécharger les documents en PDF
            </h3>
            <p className="text-sm text-blue-700 mb-4">
              Cette fonctionnalité téléchargera tous les documents validés des <strong>{inscriptionsFiltrees.length} inscription(s)</strong> actuellement affichée(s) dans le tableau ci-dessous.
              Les filtres de séjour et tranche d'âge sont pris en compte automatiquement.
              Les images (JPG, PNG) seront automatiquement converties en PDF.
            </p>

            <div className="mb-4">
              {/* Type de document */}
              <label className="block text-sm font-medium text-blue-900 mb-2">
                Type de document (optionnel)
              </label>
              <select
                value={downloadTypeDocument}
                onChange={(e) => setDownloadTypeDocument(e.target.value)}
                className="w-full border-blue-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={downloading}
              >
                <option value="">Tous les types de documents</option>
                <option value="FICHE_SANITAIRE">Fiche sanitaire uniquement</option>
                <option value="VACCINS">Vaccins uniquement</option>
                <option value="ATTESTATION_AISANCE_AQUATIQUE">Attestation d'aisance aquatique uniquement</option>
                <option value="AUTRE">Autres documents uniquement</option>
              </select>
            </div>

            <div className="bg-blue-100 border border-blue-300 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Astuce :</strong> Utilisez les filtres "Séjour" et "Tranche d'âge" ci-dessous pour affiner votre sélection avant de télécharger.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDownloadPanel(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
                disabled={downloading}
              >
                Annuler
              </button>
              <button
                onClick={handleTelechargerDocumentsGroupes}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={downloading || inscriptionsFiltrees.length === 0}
              >
                {downloading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Génération en cours...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Télécharger ({inscriptionsFiltrees.length} inscription(s))</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700">Filtres</h3>
              {modeRecherche === 'sejour' ? (
                sejourFiltre ? (
                  <p className="text-xs text-gray-500 mt-1">
                    {inscriptionsFiltrees.length} inscription(s) affichée(s) sur {inscriptions.length}
                  </p>
                ) : (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    ⚠️ Veuillez sélectionner un séjour pour afficher les inscriptions
                  </p>
                )
              ) : (
                rechercheEnfant.length >= 3 ? (
                  <p className="text-xs text-gray-500 mt-1">
                    {inscriptions.length} inscription(s) trouvée(s)
                  </p>
                ) : (
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    ℹ️ Entrez au moins 3 caractères pour rechercher
                  </p>
                )
              )}
            </div>
            {(sejourFiltre || trancheAgeFiltre || afficherAnnulees || afficherSejoursArchives || rechercheEnfant) && (
              <button
                onClick={() => {
                  setSejourFiltre('');
                  setTrancheAgeFiltre('');
                  setAfficherAnnulees(false);
                  setAfficherSejoursArchives(false);
                  setRechercheEnfant('');
                  setModeRecherche('sejour');
                }}
                className="text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>

          {/* Sélecteur de mode de recherche */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de recherche
            </label>
            <ToggleSwitch
              value={modeRecherche}
              onChange={setModeRecherche}
            />
          </div>

          {modeRecherche === 'sejour' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Séjour <span className="text-red-600">*</span> (obligatoire)
                  </label>
                  <select
                    value={sejourFiltre}
                    onChange={(e) => {
                      logger.log('🎯 Changement de séjour, afficherSejoursArchives:', afficherSejoursArchives);
                      logger.log('🎯 Séjours disponibles:', sejours.length);
                      logger.log('🎯 Séjours filtrés:', sejours.filter(sejour => afficherSejoursArchives ? sejour.archive === true : !sejour.archive));
                      setSejourFiltre(e.target.value);
                    }}
                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">-- Sélectionnez un séjour --</option>
                    {Array.isArray(sejours) && sejours
                      .filter(sejour => {
                        const result = afficherSejoursArchives ? sejour.archive === true : !sejour.archive;
                        return result;
                      })
                      .map((sejour) => (
                        <option key={sejour.id} value={sejour.id}>
                          {sejour.titre}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tranche d'âge
                  </label>
                  <select
                    value={trancheAgeFiltre}
                    onChange={(e) => setTrancheAgeFiltre(e.target.value)}
                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!sejourFiltre}
                  >
                    <option value="">Toutes les tranches</option>
                    {getTranchesAge().map((tranche) => (
                      <option key={tranche.id || tranche.value} value={tranche.value}>
                        {tranche.label}
                      </option>
                    ))}
                  </select>
                  {!sejourFiltre && (
                    <p className="text-xs text-gray-500 mt-1">
                      Sélectionnez d'abord un séjour
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Options d'affichage
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={afficherSejoursArchives}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          logger.log('📦 Toggle séjours archivés:', checked);
                          logger.log('📦 Séjours totaux:', sejours.length);
                          logger.log('📦 Séjours qui seront affichés:', sejours.filter(s => checked ? s.archive === true : !s.archive).map(s => s.titre));
                          setAfficherSejoursArchives(checked);
                          // Si on coche "Séjours archivés", cocher automatiquement "Inscriptions annulées"
                          if (checked) {
                            setAfficherAnnulees(true);
                          }
                        }}
                        className="form-checkbox h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Séjours archivés
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={afficherAnnulees}
                        onChange={(e) => setAfficherAnnulees(e.target.checked)}
                        className="form-checkbox h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Inscriptions annulées ({nombreInscriptionsAnnulees})
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Mode recherche par nom/prénom */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom ou prénom de l'enfant *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={rechercheEnfant}
                    onChange={(e) => setRechercheEnfant(e.target.value)}
                    placeholder="Entrez au moins 3 caractères..."
                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 pr-10"
                  />
                  {rechercheEnCours && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                    </div>
                  )}
                </div>
                {rechercheEnfant.length > 0 && rechercheEnfant.length < 3 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Veuillez entrer au moins 3 caractères
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  La recherche se lance automatiquement après 3 caractères
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inscriptions annulées
                </label>
                <div className="flex items-center">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={afficherAnnulees}
                      onChange={(e) => setAfficherAnnulees(e.target.checked)}
                      className="form-checkbox h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Afficher les annulées
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Légende des partenaires */}
        {modeRecherche === 'sejour' && sejourFiltre && (() => {
          const sejourSelectionne = sejours.find(s => s.id === parseInt(sejourFiltre));
          logger.log('🎨 Séjour sélectionné:', sejourSelectionne);
          logger.log('🎨 Partenaires du séjour:', sejourSelectionne?.partenaires);
          const partenaires = sejourSelectionne?.partenaires || [];

          if (partenaires.length === 0) {
            logger.log('⚠️ Aucun partenaire trouvé pour ce séjour');
            return null;
          }

          return (
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Légende des partenaires</h3>
              <div className="flex flex-wrap gap-3">
                {partenaires.map((partenaire) => (
                  <div key={partenaire.id} className="flex items-center gap-2">
                    <div
                      className="partner-legend-badge"
                      style={{ backgroundColor: partenaire.couleur }}
                      title={partenaire.nom}
                    ></div>
                    <span className="text-sm text-gray-700">{partenaire.nom}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Sélecteur de vue mobile */}
        <div className="lg:hidden mb-4">
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
            <button
              onClick={() => setMobileViewMode('cards')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mobileViewMode === 'cards'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              📋 Cartes
            </button>
            <button
              onClick={() => setMobileViewMode('table')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mobileViewMode === 'table'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              📊 Tableau
            </button>
          </div>
        </div>

        {/* Version mobile : cartes */}
        {mobileViewMode === 'cards' && (
          <div className="lg:hidden space-y-3">
            {(modeRecherche === 'sejour' && !sejourFiltre) || (modeRecherche === 'enfant' && rechercheEnfant.length < 3) ? (
              <div className="bg-white rounded-lg border border-gray-200">
                <EmptyState
                  icon="📋"
                  message={modeRecherche === 'sejour'
                    ? "Veuillez sélectionner un séjour dans les filtres ci-dessus pour afficher les inscriptions"
                    : "Veuillez entrer au moins 3 caractères pour rechercher un enfant"}
                  className="text-gray-600"
                />
              </div>
            ) : inscriptionsFiltrees.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200">
                <EmptyState
                  icon="🔍"
                  message="Aucune inscription trouvée avec ces filtres"
                />
              </div>
            ) : inscriptionsFiltrees.map((inscription, index) => {
              const couleurPartenaire = inscription.via_partenaire && inscription.partenaire_details?.couleur
                ? inscription.partenaire_details.couleur
                : null;
              const nomPartenaire = inscription.via_partenaire && inscription.partenaire_details?.nom
                ? inscription.partenaire_details.nom
                : null;

              return (
                <div key={inscription.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                        {couleurPartenaire ? (
                          <span
                            className="partner-number-badge"
                            style={{ backgroundColor: couleurPartenaire }}
                            title={`Partenaire: ${nomPartenaire}`}
                          >
                            {index + 1}
                          </span>
                        ) : (
                          <span>#{index + 1}</span>
                        )}
                        <span>- {inscription.enfant_details?.prenom} {inscription.enfant_details?.nom?.toUpperCase()}</span>
                      </h3>
                      {(() => {
                        const sejour = sejours.find(s => s.id === inscription.sejour);
                        const ageAtSejour = sejour ? calculateAgeAtSejour(
                          inscription.enfant_details?.date_naissance,
                          sejour.date_debut
                        ) : null;
                        const outOfRange = isAgeOutOfRange(inscription);

                        let tooltipText = '';
                        if (outOfRange && sejour) {
                          tooltipText = `Âge lors du séjour: ${ageAtSejour} ans\n`;
                          if (inscription.tranche_prix_details) {
                            tooltipText += `Tranche d'inscription: ${inscription.tranche_prix_details.age_min}-${inscription.tranche_prix_details.age_max} ans\n`;
                            if (inscription.tranche_prix_details.age_min !== sejour.age_min || inscription.tranche_prix_details.age_max !== sejour.age_max) {
                              tooltipText += `Tranche du séjour: ${sejour.age_min}-${sejour.age_max} ans`;
                            }
                          } else {
                            tooltipText += `Tranche du séjour: ${sejour.age_min}-${sejour.age_max} ans`;
                          }
                        }

                        return (
                          <p
                            className={`text-sm ${outOfRange ? 'text-red-600 font-bold cursor-help' : 'text-gray-600'}`}
                            title={tooltipText}
                          >
                            {ageAtSejour !== null ? `${ageAtSejour} ans` : `${inscription.enfant_details?.age} ans`}
                          </p>
                        );
                      })()}
                    </div>
                    <select
                      value={inscription.statut}
                      onChange={(e) => handleValidationChange(inscription, e.target.value)}
                      className={`text-xs border rounded px-2 py-1 ${inscription.statut === 'CONFIRMEE'
                          ? 'bg-green-50 border-green-300 text-green-800'
                          : inscription.statut === 'EN_ATTENTE'
                            ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                            : inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE'
                              ? 'bg-orange-50 border-orange-300 text-orange-800'
                              : inscription.statut === 'TERMINE'
                                ? 'bg-blue-50 border-blue-300 text-blue-800'
                                : 'bg-gray-50 border-gray-300 text-gray-800'
                        }`}
                      disabled={inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE'}
                      aria-label={`Statut de validation pour ${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                    >
                      <option value="EN_ATTENTE">En attente</option>
                      <option value="CONFIRMEE">Validé</option>
                      <option value="TERMINE">Terminé</option>
                      {inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' && (
                        <option value="EN_ATTENTE_VALIDATION_PARTENAIRE">En attente partenaire</option>
                      )}
                      <option value="ANNULEE">Annulée</option>
                    </select>
                  </div>

                  <div className="space-y-2 text-xs text-gray-700">
                    <div><span className="font-medium">Responsable :</span> {inscription.responsable_prenom} {inscription.responsable_nom}</div>
                    <div><span className="font-medium">Téléphone :</span> {inscription.responsable_telephone || '-'}</div>
                    <div><span className="font-medium">Email :</span> {inscription.responsable_email || '-'}</div>
                    <div>
                      <span className="font-medium">Paiement :</span> {formatPaiement(inscription, { showReste: true })}
                    </div>
                    <div><span className="font-medium">Aides :</span> {formatAides(inscription) || '-'}</div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Infos :</span> {getInfosStatus(inscription)}
                    </div>
                    <div><span className="font-medium">Dossier :</span> {getDossierCompletStatus(inscription)}</div>

                    {/* Documents */}
                    <div className="pt-2 border-t">
                      <div className="font-medium mb-1">Documents :</div>
                      <div className="flex flex-wrap gap-2">
                        <div className="flex items-center gap-1" title="Fiche sanitaire">
                          <span className="text-xs text-gray-600">Fiche sanit.:</span>
                          {getDocumentStatus(inscription, 'FICHE_SANITAIRE')}
                        </div>
                        <div className="flex items-center gap-1" title="Attestation aisance aquatique">
                          <span className="text-xs text-gray-600">Baignade:</span>
                          {getDocumentStatus(inscription, 'ATTESTATION_AISANCE_AQUATIQUE')}
                        </div>
                        <div className="flex items-center gap-1" title="Vaccins">
                          <span className="text-xs text-gray-600">Vaccins:</span>
                          {getDocumentStatus(inscription, 'VACCINS')}
                        </div>
                        <div className="flex items-center gap-1" title="Documents autres">
                          <span className="text-xs text-gray-600">Autres:</span>
                          {getDocumentsAutresStatus(inscription)}
                        </div>
                      </div>
                    </div>

                    {/* Autre info utile */}
                    <div className="pt-2 border-t">
                      <div className="flex justify-between items-start gap-2">
                        <span className="font-medium">Info utile :</span>
                        <button
                          onClick={() => handleEditAutreInfoMobile(inscription)}
                          className="text-blue-600 hover:text-blue-800 text-xs underline flex-1 text-right"
                          aria-label={`Éditer les informations utiles pour ${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                        >
                          {inscription.remarques_admin || '+ Ajouter'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t flex gap-2">
                    <a
                      href={`/admin/inscriptions/${inscription.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded text-xs font-medium text-center"
                      aria-label={`Voir les détails de l'inscription de ${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                    >
                      Voir détails
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Version mobile : tableau complet (identique au desktop) */}
        {mobileViewMode === 'table' && (
          <div className="lg:hidden bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto overflow-y-auto mobile-table-container">
            {(modeRecherche === 'sejour' && !sejourFiltre) || (modeRecherche === 'enfant' && rechercheEnfant.length < 3) ? (
              <EmptyState
                icon="📋"
                message={modeRecherche === 'sejour'
                  ? "Veuillez sélectionner un séjour dans les filtres ci-dessus pour afficher les inscriptions"
                  : "Veuillez entrer au moins 3 caractères pour rechercher un enfant"}
                className="text-gray-600"
              />
            ) : inscriptionsFiltrees.length === 0 ? (
              <EmptyState
                icon="🔍"
                message="Aucune inscription trouvée avec ces filtres"
              />
            ) : (
              <table className="min-w-full divide-y divide-gray-200 excel-table">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <span>#</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(0, '#'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne numéro"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('prenom')}>
                        <span>Prénom</span>
                        {sortColumn === 'prenom' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(1, 'Prénom'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne prénom"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('nom')}>
                        <span>NOM</span>
                        {sortColumn === 'nom' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(2, 'NOM'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne nom"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('age')}>
                        <span>Age</span>
                        {sortColumn === 'age' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(3, 'Age'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne âge"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('date_naissance')}>
                        <span>Date naissance</span>
                        {sortColumn === 'date_naissance' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(4, 'Date naissance'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne date de naissance"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('lieu_naissance')}>
                        <span>Lieu naissance</span>
                        {sortColumn === 'lieu_naissance' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(5, 'Lieu naissance'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne lieu de naissance"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('responsable')}>
                        <span>Responsable</span>
                        {sortColumn === 'responsable' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(6, 'Responsable'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne responsable"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('adresse')}>
                        <span>Adresse</span>
                        {sortColumn === 'adresse' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(7, 'Adresse'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne adresse"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('telephone')}>
                        <span>Téléphone</span>
                        {sortColumn === 'telephone' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(8, 'Téléphone'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne téléphone"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('email')}>
                        <span>Mail</span>
                        {sortColumn === 'email' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(9, 'Mail'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne mail"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100">
                      <div className="flex items-center gap-1" onClick={() => handleSort('statut')}>
                        <span>Validation</span>
                        {sortColumn === 'statut' && <span className="text-primary-600">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(10, 'Validation'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne validation"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <span>Aide</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(11, 'Aide'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne aide"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <span>Paiement</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyColumn(12, 'Paiement'); }}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copier toute la colonne"
                          aria-label="Copier la colonne paiement"
                        >
                          📋
                        </button>
                      </div>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Dossier</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Infos</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fiche sanit.</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Baignade</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Vaccins</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Docs autres</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Info utile</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inscriptionsFiltrees.map((inscription, index) => {
                    const couleurPartenaire = inscription.via_partenaire && inscription.partenaire_details?.couleur
                      ? inscription.partenaire_details.couleur
                      : null;
                    const nomPartenaire = inscription.via_partenaire && inscription.partenaire_details?.nom
                      ? inscription.partenaire_details.nom
                      : null;

                    return (
                      <tr key={inscription.id} className="hover:bg-gray-50">
                        <td
                          className="px-2 py-2 whitespace-nowrap text-xs font-medium"
                          style={{
                            backgroundColor: couleurPartenaire || 'transparent',
                            color: couleurPartenaire ? 'white' : '#6B7280'
                          }}
                          title={nomPartenaire ? `Partenaire: ${nomPartenaire}` : ''}
                        >
                          {index + 1}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">{inscription.enfant_details?.prenom || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs font-medium text-gray-900">{inscription.enfant_details?.nom?.toUpperCase() || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          {(() => {
                            const sejour = sejours.find(s => s.id === inscription.sejour);

                            if (!sejour) {
                              return <span className="text-gray-900">{inscription.enfant_details?.age || '-'}</span>;
                            }

                            const ageAtSejour = calculateAgeAtSejour(
                              inscription.enfant_details?.date_naissance,
                              sejour.date_debut
                            );

                            const outOfRange = isAgeOutOfRange(inscription);

                            let tooltipText = '';
                            if (outOfRange) {
                              tooltipText = `Âge lors du séjour: ${ageAtSejour} ans\nTranche du séjour: ${sejour.age_min}-${sejour.age_max} ans`;
                              if (inscription.tranche_prix_details) {
                                tooltipText += `\nTranche d'inscription: ${inscription.tranche_prix_details.age_min}-${inscription.tranche_prix_details.age_max} ans`;
                              }
                            }

                            return (
                              <span
                                className={outOfRange ? 'age-out-of-range cursor-help' : 'text-gray-900'}
                                title={tooltipText}
                              >
                                {ageAtSejour !== null ? ageAtSejour : inscription.enfant_details?.age || '-'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          {inscription.enfant_details?.date_naissance ? new Date(inscription.enfant_details.date_naissance).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">{inscription.enfant_details?.lieu_naissance || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          {inscription.responsable_nom && inscription.responsable_prenom ? `${inscription.responsable_prenom} ${inscription.responsable_nom}` : '-'}
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-900 max-w-xs truncate">
                          {inscription.responsable_adresse ? `${inscription.responsable_adresse}, ${inscription.responsable_code_postal} ${inscription.responsable_ville}` : '-'}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">{inscription.responsable_telephone || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">{inscription.responsable_email || '-'}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          <select
                            value={inscription.statut}
                            onChange={(e) => handleValidationChange(inscription, e.target.value)}
                            className={`text-xs border rounded px-1 py-0.5 ${inscription.statut === 'CONFIRMEE'
                                ? 'bg-green-50 border-green-300 text-green-800'
                                : inscription.statut === 'EN_ATTENTE'
                                  ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                                  : inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE'
                                    ? 'bg-orange-50 border-orange-300 text-orange-800'
                                    : inscription.statut === 'TERMINE'
                                      ? 'bg-blue-50 border-blue-300 text-blue-800'
                                      : 'bg-gray-50 border-gray-300 text-gray-800'
                              }`}
                            disabled={inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE'}
                            aria-label={`Statut de validation pour ${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                          >
                            <option value="EN_ATTENTE">En attente</option>
                            <option value="CONFIRMEE">Validé</option>
                            <option value="TERMINE">Terminé</option>
                            {inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' && (
                              <option value="EN_ATTENTE_VALIDATION_PARTENAIRE">En attente partenaire</option>
                            )}
                            <option value="ANNULEE">Annulée</option>
                          </select>
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-900 max-w-[160px] truncate">{formatAidesCompact(inscription)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          {formatPaiement(inscription, { showReste: true })}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{getDossierCompletStatus(inscription)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{getInfosStatus(inscription)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{getDocumentStatus(inscription, 'FICHE_SANITAIRE')}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{getDocumentStatus(inscription, 'ATTESTATION_AISANCE_AQUATIQUE')}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{getDocumentStatus(inscription, 'VACCINS')}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{getDocumentsAutresStatus(inscription)}</td>
                        <td className="px-2 py-2 text-xs text-gray-600 cursor-pointer hover:bg-gray-100" onClick={() => handleEditAutreInfoMobile(inscription)} title="Cliquer pour éditer">
                          {inscription.remarques_admin || '+ Ajouter'}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          <a
                            href={`/admin/inscriptions/${inscription.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-primary-600 hover:bg-primary-700 text-white px-2 py-1 rounded text-xs text-center inline-block"
                            aria-label={`Voir les détails de l'inscription de ${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                          >
                            Voir
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Version desktop : tableau complet */}
        <div className="hidden lg:block bg-white rounded-lg shadow overflow-x-auto overflow-y-auto relative desktop-table-container">
          <table className="min-w-full divide-y divide-gray-200 excel-table">
            <thead className="bg-gray-50 sticky top-0 z-40">
              <tr>
                <th className="sticky left-0 z-30 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50 sticky-header-actions">
                  #
                </th>
                <th
                  className="sticky z-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100 bg-gray-50 sticky-header-nom-position"
                >
                  <div className="flex items-center space-x-1">
                    <span className="cursor-pointer" onClick={() => handleSort('prenom')}>Prénom</span>
                    {sortColumn === 'prenom' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                    <span
                      className="copy-column-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyColumn(1, 'Prénom');
                      }}
                      title="Copier toute la colonne"
                    >
                      📋
                    </span>
                  </div>
                </th>
                <th
                  className="sticky z-30 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100 bg-gray-50 sticky-header-prenom-position"
                >
                  <div className="flex items-center space-x-1">
                    <span className="cursor-pointer" onClick={() => handleSort('nom')}>NOM</span>
                    {sortColumn === 'nom' && (
                      <span className="text-primary-600">
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                    <span
                      className="copy-column-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyColumn(2, 'NOM');
                      }}
                      title="Copier toute la colonne"
                    >
                      📋
                    </span>
                  </div>
                </th>
                <SortableHeader column="age" label="Age" columnIndex={3} />
                <SortableHeader column="date_naissance" label="Date de naissance" columnIndex={4} />
                <SortableHeader column="lieu_naissance" label="Lieu de naissance" columnIndex={5} />
                <SortableHeader column="responsable" label="Responsable légal" columnIndex={6} />
                <SortableHeader column="adresse" label="Adresse" columnIndex={7} />
                <SortableHeader column="telephone" label="Téléphone" columnIndex={8} />
                <SortableHeader column="email" label="Mail" columnIndex={9} />
                <SortableHeader column="statut" label="VALIDATION" columnIndex={10} />
                <CopyableHeader label="Aide" columnIndex={11} />
                <CopyableHeader label="Paiement" columnIndex={12} />
                <CopyableHeader label="Dossier complet" columnIndex={13} />
                <CopyableHeader label="Informations" columnIndex={14} />
                <CopyableHeader label="Fiche sanitaire" columnIndex={15} />
                <CopyableHeader label="Baignade" columnIndex={16} />
                <CopyableHeader label="Vaccins" columnIndex={17} />
                <CopyableHeader label="Documents autres" columnIndex={18} />
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  AUTRE INFO UTILE
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inscriptionsFiltrees.map((inscription, index) => {
                // Déterminer la couleur de fond si inscription via partenaire ET que le partenaire existe toujours
                const hasValidPartenaire = inscription.via_partenaire && inscription.partenaire_details?.couleur;
                const couleurPartenaire = hasValidPartenaire
                  ? inscription.partenaire_details.couleur
                  : 'white';
                const tooltipPartenaire = hasValidPartenaire
                  ? `Partenaire: ${inscription.partenaire_details.nom}`
                  : '';

                return (
                  <tr key={inscription.id} className="hover:bg-gray-50">
                    <td
                      className="sticky left-0 z-30 px-2 py-2 whitespace-nowrap text-sm font-medium sticky-cell-actions"
                      style={{
                        backgroundColor: couleurPartenaire,
                        color: hasValidPartenaire ? 'white' : '#4B5563'
                      }}
                      title={tooltipPartenaire}
                    >
                      {index + 1}
                    </td>
                    <td className="sticky z-20 px-3 py-2 whitespace-nowrap text-sm text-gray-900 bg-white sticky-cell-nom-position">
                      {inscription.enfant_details?.prenom || '-'}
                    </td>
                    <td className="sticky z-30 px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 bg-white sticky-cell-prenom-position" title={inscription.enfant_details?.nom || ''}>
                      {inscription.enfant_details?.nom?.toUpperCase() || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {(() => {
                        const sejour = sejours.find(s => s.id === inscription.sejour);
                        if (!sejour) {
                          return <span className="text-gray-900">{inscription.enfant_details?.age || '-'}</span>;
                        }

                        const ageAtSejour = calculateAgeAtSejour(
                          inscription.enfant_details?.date_naissance,
                          sejour.date_debut
                        );
                        const outOfRange = isAgeOutOfRange(inscription);

                        let tooltipText = '';
                        if (outOfRange) {
                          tooltipText = `Âge lors du séjour: ${ageAtSejour} ans\nTranche du séjour: ${sejour.age_min}-${sejour.age_max} ans`;
                          if (inscription.tranche_prix_details) {
                            tooltipText += `\nTranche d'inscription: ${inscription.tranche_prix_details.age_min}-${inscription.tranche_prix_details.age_max} ans`;
                          }
                        }

                        return (
                          <span
                            className={outOfRange ? 'age-out-of-range cursor-help' : 'text-gray-900'}
                            title={tooltipText}
                          >
                            {ageAtSejour !== null ? ageAtSejour : inscription.enfant_details?.age || '-'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {inscription.enfant_details?.date_naissance
                        ? new Date(inscription.enfant_details.date_naissance).toLocaleDateString('fr-FR')
                        : '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {inscription.enfant_details?.lieu_naissance || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {inscription.responsable_nom && inscription.responsable_prenom
                        ? `${inscription.responsable_prenom} ${inscription.responsable_nom}`
                        : '-'}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate">
                      {inscription.responsable_adresse
                        ? `${inscription.responsable_adresse}, ${inscription.responsable_code_postal} ${inscription.responsable_ville}`
                        : '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {inscription.responsable_telephone || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {inscription.responsable_email || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <select
                        value={inscription.statut}
                        onChange={(e) => handleValidationChange(inscription, e.target.value)}
                        className={`text-xs border rounded px-2 py-1 ${inscription.statut === 'CONFIRMEE'
                            ? 'bg-green-50 border-green-300 text-green-800'
                            : inscription.statut === 'EN_ATTENTE'
                              ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                              : inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE'
                                ? 'bg-orange-50 border-orange-300 text-orange-800'
                                : inscription.statut === 'TERMINE'
                                  ? 'bg-blue-50 border-blue-300 text-blue-800'
                                  : 'bg-gray-50 border-gray-300 text-gray-800'
                          }`}
                        disabled={inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE'}
                      >
                        <option value="EN_ATTENTE">En attente</option>
                        <option value="CONFIRMEE">Validé</option>
                        <option value="TERMINE">Terminé</option>
                        {inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' && (
                          <option value="EN_ATTENTE_VALIDATION_PARTENAIRE">En attente partenaire</option>
                        )}
                        <option value="ANNULEE">Annulée</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 max-w-[180px] truncate">
                      {formatAidesCompact(inscription)}
                    </td>
                    <td
                      className={`px-3 py-2 whitespace-nowrap text-sm text-gray-900 ${hasPaiementsEnAttente(inscription) ? 'cursor-pointer hover:bg-yellow-50' : 'cursor-help'
                        }`}
                      title={getPaiementDetails(inscription)}
                      onClick={(e) => hasPaiementsEnAttente(inscription) && handleCellClick(e, () => navigate(`/admin/inscriptions/${inscription.id}`))}
                    >
                      {formatPaiement(inscription)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {getDossierCompletStatus(inscription)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-center">
                      {getInfosStatus(inscription)}
                    </td>
                    <td
                      className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-center cursor-pointer hover:bg-gray-100"
                      onClick={(e) => handleCellClick(e, () => navigate(`/admin/inscriptions/${inscription.id}`))}
                      title="Cliquez pour gérer les documents"
                    >
                      {getDocumentStatus(inscription, 'FICHE_SANITAIRE')}
                    </td>
                    <td
                      className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-center cursor-pointer hover:bg-gray-100"
                      onClick={(e) => handleCellClick(e, () => navigate(`/admin/inscriptions/${inscription.id}`))}
                      title="Cliquez pour gérer les documents"
                    >
                      {getDocumentStatus(inscription, 'ATTESTATION_AISANCE_AQUATIQUE')}
                    </td>
                    <td
                      className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-center cursor-pointer hover:bg-gray-100"
                      onClick={(e) => handleCellClick(e, () => navigate(`/admin/inscriptions/${inscription.id}`))}
                      title="Cliquez pour gérer les documents"
                    >
                      {getDocumentStatus(inscription, 'VACCINS')}
                    </td>
                    <td
                      className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-center cursor-pointer hover:bg-gray-100"
                      onClick={(e) => handleCellClick(e, () => navigate(`/admin/inscriptions/${inscription.id}`))}
                      title="Cliquez pour gérer les documents autres"
                    >
                      {getDocumentsAutresStatus(inscription)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900 min-w-[200px]">
                      {editingCell?.inscriptionId === inscription.id &&
                        editingCell?.field === 'remarques_admin' ? (
                        <input
                          type="text"
                          defaultValue={inscription.remarques_admin || ''}
                          autoFocus
                          onBlur={(e) => handleBlur(inscription, 'remarques_admin', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          className="w-full border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <div
                          onDoubleClick={() => handleDoubleClick(inscription.id, 'remarques_admin')}
                          className="cursor-text hover:bg-gray-100 px-2 py-1 rounded min-h-[28px]"
                        >
                          {inscription.remarques_admin || '(Double-clic pour éditer)'}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <a
                        href={`/admin/inscriptions/${inscription.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors text-center inline-block"
                      >
                        Gérer
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {((modeRecherche === 'sejour' && !sejourFiltre) || (modeRecherche === 'enfant' && rechercheEnfant.length < 3)) && (
            <div className="text-center py-12 text-gray-600 bg-white rounded-lg border border-gray-200">
              <div className="mb-2 text-3xl">📋</div>
              {modeRecherche === 'sejour' ? (
                <>
                  <div className="font-medium mb-1">Sélection de séjour requise</div>
                  <div className="text-sm">Veuillez sélectionner un séjour dans les filtres ci-dessus pour afficher les inscriptions</div>
                </>
              ) : (
                <>
                  <div className="font-medium mb-1">Recherche requise</div>
                  <div className="text-sm">Veuillez entrer au moins 3 caractères pour rechercher un enfant</div>
                </>
              )}
            </div>
          )}

          {((modeRecherche === 'sejour' && sejourFiltre) || (modeRecherche === 'enfant' && rechercheEnfant.length >= 3)) && inscriptionsFiltrees.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucune inscription trouvée avec ces filtres
            </div>
          )}
        </div>

        {/* Statistiques */}
        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {inscriptionsFiltrees.length}
              </div>
              <div className="text-sm text-gray-600">Total inscriptions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {inscriptionsFiltrees.filter((i) => i.statut === 'CONFIRMEE').length}
              </div>
              <div className="text-sm text-gray-600">Validées</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {inscriptionsFiltrees.filter((i) => i.statut === 'EN_ATTENTE').length}
              </div>
              <div className="text-sm text-gray-600">En attente</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {inscriptionsFiltrees.reduce((sum, i) => sum + (parseFloat(i.montant_paye) || 0), 0).toFixed(2)}€
              </div>
              <div className="text-sm text-gray-600">Total payé</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardAdmin;
